import {
  createNestablePublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  Configuration,
} from '@azure/msal-browser';
import { config } from '@/config';
import { logger } from '@/utils/logger';
import { setTelemetryUser } from '@/services/telemetry';

// Session-storage key used to guard against redirect loops during interactive
// browser sign-in. Defined here so all references share a single literal.
const REDIRECT_GUARD = 'crg:auth:redirecting';

// NAA works only when the Office host injects this bridge global. MSAL checks
// the same `window.nestedAppAuthBridge` internally: when it is absent,
// createNestablePublicClientApplication does NOT throw, it silently returns a
// standard (non-nested) client. So we detect the bridge ourselves to know which
// mode we are really in, rather than relying on a creation failure that never
// happens. Absent in a plain browser, where we sign in via interactive redirect.
function isNaaBridgeAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { nestedAppAuthBridge?: unknown }).nestedAppAuthBridge !== undefined
  );
}

// MSAL configuration shared by the nested (NAA) and standalone clients.
// `supportsNestedAppAuth` enables the Office host broker path; the broker
// returns Microsoft Graph tokens client-side without a backend (no BFF/OBO).
const msalConfig: Configuration = {
  auth: {
    clientId: config.auth.clientId,
    authority: config.auth.authority,
    redirectUri: config.auth.redirectUri,
    supportsNestedAppAuth: true,
    clientCapabilities: ['CP1'],
  },
  cache: {
    // localStorage so the token cache survives taskpane restarts and Outlook
    // sessions - sessionStorage forced a fresh auth round-trip every session.
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: true,
  },
};

export class AuthService {
  // Either a nested (NAA) client or a standalone fallback client.
  private msalInstance: IPublicClientApplication | null = null;
  // True when the active instance is the NAA nested client.
  private usingNestedClient = false;
  private account: AccountInfo | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.initialized) {
      return;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      // Check if we have valid config
      if (!config.auth.clientId) {
        logger.warn('No Entra client ID configured. Authentication disabled.');
        this.initialized = true;
        return;
      }

      // Decide the mode from the bridge BEFORE creating the client (the factory
      // returns a nested client when the bridge is present, a standard client
      // when it is absent, and never throws). Inside an Office host -> nested,
      // silent. In a plain browser -> standard, interactive redirect.
      this.usingNestedClient = isNaaBridgeAvailable();
      this.msalInstance = await createNestablePublicClientApplication(msalConfig);

      if (!this.usingNestedClient) {
        // Standard client in a browser: a redirect sign-in returns to
        // /taskpane.html with the result in the URL; process it so the account
        // is captured on the way back in.
        const redirectResult = await this.msalInstance.handleRedirectPromise();
        if (redirectResult?.account) {
          this.account = redirectResult.account;
        }
      }

      // Pick up any cached account (relevant mainly for the standalone path).
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.account = accounts[0];
      }

      this.initialized = true;
      console.log(
        '[CRG]',
        'Auth initialized (nested:',
        this.usingNestedClient,
        '), account:',
        this.account?.username || 'none',
      );
    } catch (error) {
      logger.error('Auth initialization failed:', error);
      this.initialized = true; // Mark as initialized even on error to prevent loops
      throw error;
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  isAuthenticated(): boolean {
    return this.account !== null;
  }

  getAccount(): AccountInfo | null {
    return this.account;
  }

  async login(): Promise<AccountInfo | null> {
    if (!this.msalInstance) {
      throw new Error('Auth not initialized. Please configure Entra credentials.');
    }

    // Acquire a token to establish the account. Inside Outlook this is silent
    // via the NAA broker; in a browser getAccessToken starts an interactive
    // redirect when there is no account yet.
    await this.getGraphToken();
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.account = accounts[0];
    }
    return this.account;
  }

  async logout(): Promise<void> {
    // NAA does not support logoutPopup/logoutRedirect (they throw). Clear the
    // local account only; the host owns the actual Office sign-in session.
    this.account = null;
  }

  async getAccessToken(scopes?: string[], forceRefresh = false): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('Auth not initialized');
    }

    const tokenScopes = scopes || config.auth.scopes;

    try {
      // Silent acquisition. Under NAA the host broker resolves the active
      // Office user, so an explicit account is not required. forceRefresh
      // bypasses the cached access token and re-mints one from the refresh
      // token, so membership changes (added/removed site access) take effect
      // without waiting for the cached token to expire.
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account: this.account ?? undefined,
        forceRefresh,
      });
      return this.captureToken(response);
    } catch (error) {
      // Inside Outlook (NAA): no interactive path; the host owns sign-in, so
      // surface the error for the caller to display.
      if (this.usingNestedClient) {
        logger.error('Silent token acquisition failed (NAA):', error);
        throw error;
      }
      // In a plain browser (standalone client): start an interactive full-page
      // redirect sign-in. A sessionStorage guard prevents a redirect loop if the
      // user returns still without an account (cancelled sign-in).
      if (sessionStorage.getItem(REDIRECT_GUARD)) {
        sessionStorage.removeItem(REDIRECT_GUARD);
        logger.error('Interactive sign-in did not yield an account:', error);
        throw error;
      }
      sessionStorage.setItem(REDIRECT_GUARD, '1');
      logger.info('No account in browser; starting interactive redirect sign-in');
      await this.msalInstance.acquireTokenRedirect({ scopes: tokenScopes });
      // acquireTokenRedirect navigates away; nothing after this runs.
      throw error;
    }
  }

  // Acquire a Microsoft Graph token for SharePoint drive operations via NAA.
  async getGraphToken(scopes?: string[], forceRefresh = false): Promise<string> {
    return this.getAccessToken(scopes || config.auth.graphScopes, forceRefresh);
  }

  // Acquire a SharePoint-audience token (different resource from Graph) for the
  // SharePoint REST API. Used only for the lock-tolerant list-item rename.
  async getSharePointToken(forceRefresh = false): Promise<string> {
    return this.getAccessToken([config.sharePoint.restScope], forceRefresh);
  }

  async acquireGraphWithClaims(claims: string): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('Auth not initialized');
    }
    return this.captureToken(
      await this.msalInstance.acquireTokenSilent({
        scopes: config.auth.graphScopes,
        account: this.account ?? undefined,
        claims,
      }),
    );
  }

  private captureToken(response: AuthenticationResult): string {
    if (response.account) {
      this.account = response.account;
      setTelemetryUser(response.account.localAccountId, response.account.username);
    }
    sessionStorage.removeItem(REDIRECT_GUARD);
    return response.accessToken;
  }
}

export const authService = new AuthService();
